import React, { useMemo, useState } from 'react';
import { gameplayerAPI, matchplayersAPI } from '../../services/api';
import { parseLineupPasteNumberFirst } from '../../utils/playerListParser';
import {
    toStr,
    lineupPlayerKey,
    getLineupPlayerProfile,
    playerDisplayNameFromEntry,
    rosterEntryKey,
} from '../../utils/tickerLineupUtils';

const POSITION_OPTIONS = ['TW', 'LV', 'IV', 'RV', 'LM', 'ZM', 'DM', 'OM', 'RM', 'LA', 'RA', 'ST', 'HS'];

function PlayerRow({ entry, availablePlayers, onRemove, onUpdate }) {
    const profile = getLineupPlayerProfile(entry);
    const name = playerDisplayNameFromEntry(entry);
    const isStarter = entry.type !== 1;
    const key = lineupPlayerKey(entry);
    const roster = availablePlayers?.find((p) => rosterEntryKey(p) === key || toStr(p.plid?._id || p.plid) === toStr(entry.plid?._id || entry.plid));
    const num = entry.num ?? roster?.num ?? profile.num ?? '';

    return (
        <div className="flex items-center gap-2 py-1.5 border-b last:border-0">
            <div className="flex-none w-8 text-center font-mono text-sm text-slate-500">{num || ''}</div>
            <div className="flex-1 text-sm font-medium">{name}</div>
            <div className="flex-none">
                <input
                    type="text"
                    value={entry.positionDetail || entry.field || ''}
                    onChange={(e) => onUpdate(entry._id, { positionDetail: e.target.value, field: e.target.value })}
                    className="input input-xs input-bordered w-16 text-center"
                    placeholder="Pos"
                    list="positions-list"
                />
            </div>
            <div className="flex-none flex items-center gap-1">
                <button
                    className={`btn btn-xs ${entry.isCaptain ? 'btn-warning' : 'btn-ghost'}`}
                    onClick={() => onUpdate(entry._id, { isCaptain: !entry.isCaptain })}
                    title="Kapitän"
                >C</button>
                <button
                    className={`btn btn-xs ${isStarter ? 'btn-success' : 'btn-ghost'}`}
                    onClick={() => onUpdate(entry._id, { type: isStarter ? 1 : 0, isStarter: !isStarter })}
                    title={isStarter ? 'Starter' : 'Einwechslung'}
                >
                    {isStarter ? '▶' : '⬇'}
                </button>
                <button className="btn btn-xs btn-ghost text-error" onClick={() => onRemove(entry._id)}>✕</button>
            </div>
        </div>
    );
}

function rosterLabel(entry) {
    const num = entry.num ? `#${entry.num} ` : '';
    const name = entry.plid?.surname
        ? `${entry.plid.surname}${entry.plid.prename ? `, ${entry.plid.prename}` : ''}`
        : '?';
    return `${num}${name}`.trim();
}

function AddPlayerForm({ teamId, teamName, availablePlayers, existingKeys, onAdd, useMatchPlayers }) {
    const [playerKey, setPlayerKey] = useState('');
    const [search, setSearch] = useState('');
    const [pos, setPos] = useState('');
    const [isStarter, setIsStarter] = useState(true);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);

    const eligible = availablePlayers
        .filter((entry) => toStr(entry.tid) === toStr(teamId) && !existingKeys.includes(rosterEntryKey(entry)));

    const q = search.trim().toLowerCase();
    const filtered = eligible.filter((entry) => {
        if (!q) return true;
        const hay = `${entry.num || ''} ${entry.plid?.surname || ''} ${entry.plid?.prename || ''}`.toLowerCase();
        return hay.includes(q);
    });

    const pickPlayer = (entry) => {
        setPlayerKey(rosterEntryKey(entry));
        setSearch(rosterLabel(entry));
        setOpen(false);
    };

    const handleAdd = () => {
        if (!playerKey) { setError('Bitte Spieler wählen'); return; }
        setError('');
        const entry = eligible.find((item) => rosterEntryKey(item) === playerKey);
        if (!entry) { setError('Spieler nicht gefunden'); return; }

        if (useMatchPlayers || entry.source === 'matchplayer') {
            onAdd({
                matchPlayerId: entry.matchPlayerId?._id || entry.matchPlayerId,
                num: entry.num ?? null,
                field: pos || 'N/A',
                positionDetail: pos,
                type: isStarter ? 0 : 1,
                isStarter,
                tid: teamId,
            });
        } else {
            onAdd({
                plid: entry.plid?._id || entry.plid,
                num: entry.num ?? null,
                field: pos || 'N/A',
                positionDetail: pos,
                type: isStarter ? 0 : 1,
                isStarter,
                tid: teamId,
            });
        }

        setPlayerKey('');
        setSearch('');
        setPos('');
    };

    return (
        <div className="mt-2 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs font-semibold text-slate-600 mb-2">Spieler hinzufügen – {teamName}</p>
            {error && <p className="text-error text-xs mb-2">{error}</p>}
            <div className="flex gap-2 flex-wrap items-start">
                <div className="relative flex-1 min-w-[180px]">
                    <input
                        type="text"
                        value={search}
                        placeholder="Spieler suchen…"
                        onChange={(e) => { setSearch(e.target.value); setPlayerKey(''); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        onBlur={() => setTimeout(() => setOpen(false), 150)}
                        className="input input-bordered input-sm w-full"
                    />
                    {open && filtered.length > 0 && (
                        <ul className="absolute z-20 mt-1 w-full max-h-44 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg text-sm">
                            {filtered.slice(0, 25).map((entry) => {
                                const key = rosterEntryKey(entry);
                                return (
                                    <li key={key}>
                                        <button
                                            type="button"
                                            className={`w-full text-left px-3 py-2 hover:bg-slate-100 ${playerKey === key ? 'bg-blue-50 font-medium' : ''}`}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => pickPlayer(entry)}
                                        >
                                            {rosterLabel(entry)}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                <input
                    type="text"
                    value={pos}
                    onChange={(e) => setPos(e.target.value)}
                    placeholder="Position"
                    className="input input-bordered input-sm w-20"
                    list="positions-list"
                />
                <label className="flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={isStarter} onChange={(e) => setIsStarter(e.target.checked)} className="checkbox checkbox-sm" />
                    Starter
                </label>
                <button className="btn btn-sm btn-primary" onClick={handleAdd}>+ Hinzufügen</button>
            </div>
        </div>
    );
}

function OpponentLineupTools({ teamId, teamName, projectId, gameId, onImported, onLineupRefresh }) {
    const [rawText, setRawText] = useState('');
    const [busy, setBusy] = useState('');
    const [message, setMessage] = useState('');

    const parsed = useMemo(() => parseLineupPasteNumberFirst(rawText), [rawText]);

    const handlePasteImport = async () => {
        if (!parsed.length || !projectId || !teamId) return;
        setBusy('import');
        setMessage('');
        try {
            const res = await matchplayersAPI.bulkUpsert({
                pid: projectId,
                tid: teamId,
                players: parsed,
            });
            const lineupRes = await matchplayersAPI.applyLineup({
                gid: gameId,
                tid: teamId,
                matchPlayerIds: (res.data?.matchplayers || []).map((mp) => mp._id),
                replace: true,
            });
            onImported?.(res.data?.matchplayers || []);
            const fullLineup = await gameplayerAPI.getByGame(gameId);
            onLineupRefresh?.(fullLineup.data || []);
            setMessage(`${parsed.length} Spieler importiert, ${lineupRes.data?.count || 0} in Aufstellung übernommen.`);
            setRawText('');
        } catch (err) {
            setMessage(err.response?.data?.message || 'Import fehlgeschlagen');
        } finally {
            setBusy('');
        }
    };

    const handleLoadPool = async () => {
        if (!projectId || !teamId) return;
        setBusy('load');
        setMessage('');
        try {
            const res = await matchplayersAPI.applyLineup({ gid: gameId, tid: teamId });
            const fullLineup = await gameplayerAPI.getByGame(gameId);
            onLineupRefresh?.(fullLineup.data || []);
            setMessage(`${res.data?.count || 0} Spieler aus Gegner-Kader übernommen.`);
        } catch (err) {
            setMessage(err.response?.data?.message || 'Laden fehlgeschlagen');
        } finally {
            setBusy('');
        }
    };

    return (
        <div className="mx-4 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
            <p className="text-xs font-semibold text-amber-900">
                Gegner-Kader – {teamName}
            </p>
            <p className="text-xs text-amber-800">
                Liste aus dem Portal einfügen (RNr. zuerst) oder gespeicherten Kader laden. Feine Positionen danach per Hand setzen.
            </p>
            <textarea
                className="textarea textarea-bordered textarea-xs w-full font-mono h-28"
                placeholder="01&#10;Noah Ender&#10;Tor&#10;0 0 0 0"
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
            />
            {parsed.length > 0 && (
                <p className="text-xs text-slate-600">{parsed.length} Spieler erkannt</p>
            )}
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    className="btn btn-xs btn-primary"
                    disabled={!parsed.length || !!busy}
                    onClick={handlePasteImport}
                >
                    {busy === 'import' ? 'Import…' : 'Einfügen & Aufstellung'}
                </button>
                <button
                    type="button"
                    className="btn btn-xs btn-outline"
                    disabled={!!busy}
                    onClick={handleLoadPool}
                >
                    {busy === 'load' ? 'Laden…' : 'Gespeicherten Kader laden'}
                </button>
            </div>
            {message && <p className="text-xs text-slate-600">{message}</p>}
        </div>
    );
}

export default function LineupTab({
    game,
    gameId,
    lineup,
    availablePlayers,
    onLineupChange,
    trackedTeamId,
    onMatchPlayersChange,
}) {
    const [saving, setSaving] = useState(null);
    const [error, setError] = useState('');

    const homeId = toStr(game?.hid?._id || game?.hid);
    const awayId = toStr(game?.aid?._id || game?.aid);
    const homeName = game?.hid?.name || 'Heim';
    const awayName = game?.aid?.name || 'Gast';
    const projectId = toStr(game?.pid?._id || game?.pid);
    const trackedId = toStr(trackedTeamId);

    const forTeam = (teamId) => lineup.filter((e) => toStr(e.tid) === toStr(teamId));
    const existingKeys = (teamId) => forTeam(teamId).map((e) => lineupPlayerKey(e));

    const handleAdd = async (teamId, data) => {
        setError('');
        setSaving('add');
        try {
            const payload = {
                gid: gameId,
                pid: projectId,
                tid: teamId,
                num: data.num ?? null,
                field: data.field || 'N/A',
                positionDetail: data.positionDetail || '',
                type: data.type,
                isStarter: data.isStarter,
                isCaptain: data.isCaptain || false,
                minute: 0,
            };

            if (data.matchPlayerId) payload.matchPlayerId = data.matchPlayerId;
            else payload.plid = data.plid;

            const res = await gameplayerAPI.create(payload);
            onLineupChange((prev) => [...prev, res.data]);
        } catch (err) {
            setError(err.response?.data?.message || 'Fehler beim Hinzufügen');
        } finally {
            setSaving(null);
        }
    };

    const handleUpdate = async (id, updates) => {
        setSaving(id);
        try {
            await gameplayerAPI.update(id, updates);
            onLineupChange((prev) => prev.map((e) => (e._id === id ? { ...e, ...updates } : e)));
        } catch (err) {
            setError('Fehler beim Aktualisieren');
        } finally {
            setSaving(null);
        }
    };

    const handleRemove = async (id) => {
        if (!window.confirm('Spieler aus Aufstellung entfernen?')) return;
        setSaving(id);
        try {
            await gameplayerAPI.delete(id);
            onLineupChange((prev) => prev.filter((e) => e._id !== id));
        } catch (err) {
            setError('Fehler beim Entfernen');
        } finally {
            setSaving(null);
        }
    };

    const TeamSection = ({ teamId, teamName, isOpponent }) => {
        const players = forTeam(teamId);
        const starters = players.filter((p) => p.type !== 1);
        const subs = players.filter((p) => p.type === 1);

        return (
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <div className="bg-slate-800 text-white px-4 py-2 flex items-center justify-between">
                    <span className="font-bold">{teamName}</span>
                    <span className="text-xs text-slate-300">{starters.length} Starter · {subs.length} Bank</span>
                </div>

                {isOpponent && (
                    <OpponentLineupTools
                        teamId={teamId}
                        teamName={teamName}
                        projectId={projectId}
                        gameId={gameId}
                        onImported={(matchPlayers) => onMatchPlayersChange?.(teamId, matchPlayers)}
                        onLineupRefresh={onLineupChange}
                    />
                )}

                <div className="px-4">
                    {starters.length > 0 && (
                        <>
                            <p className="text-xs text-slate-500 mt-3 mb-1 font-semibold uppercase tracking-wide">Startelf</p>
                            {starters.map((e) => (
                                <PlayerRow key={e._id} entry={e} availablePlayers={availablePlayers} onRemove={handleRemove} onUpdate={handleUpdate} />
                            ))}
                        </>
                    )}
                    {subs.length > 0 && (
                        <>
                            <p className="text-xs text-slate-500 mt-3 mb-1 font-semibold uppercase tracking-wide">Ersatzbank</p>
                            {subs.map((e) => (
                                <PlayerRow key={e._id} entry={e} availablePlayers={availablePlayers} onRemove={handleRemove} onUpdate={handleUpdate} />
                            ))}
                        </>
                    )}
                    <AddPlayerForm
                        teamId={teamId}
                        teamName={teamName}
                        availablePlayers={availablePlayers}
                        existingKeys={existingKeys(teamId)}
                        onAdd={(data) => handleAdd(teamId, data)}
                        useMatchPlayers={isOpponent}
                    />
                    <div className="py-2" />
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-4">
            {error && (
                <div className="alert alert-error text-sm">
                    <span>⚠️ {error}</span>
                    <button className="btn btn-xs btn-ghost ml-auto" onClick={() => setError('')}>✕</button>
                </div>
            )}
            {saving === 'add' && <div className="text-center text-slate-400 text-sm">Speichern...</div>}
            <TeamSection teamId={homeId} teamName={homeName} isOpponent={trackedId ? homeId !== trackedId : false} />
            <TeamSection teamId={awayId} teamName={awayName} isOpponent={trackedId ? awayId !== trackedId : true} />
            <datalist id="positions-list">
                {POSITION_OPTIONS.map((p) => <option key={p} value={p} />)}
            </datalist>
        </div>
    );
}
